import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Car, Wrench, Phone, Mail, MapPin, Copy, ExternalLink, Pencil, Save,
} from "lucide-react";
import { toast } from "sonner";

const CLIENT_TYPES = ["Particular", "Garantia", "Frota", "Body Shop"];

export default function ClienteDetalhe() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [portal, setPortal] = useState(null);
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get(`/clients/${id}`);
    setData(data);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPortalLink = async (reset = false) => {
    const { data } = await api.get(`/clients/${id}/portal-link`, { params: reset ? { reset: true } : {} });
    setPortal(data);
    return data;
  };

  const copyLink = async () => {
    const p = portal || (await openPortalLink());
    const url = `${window.location.origin}${p.portal_url}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado para a área de transferência");
    } catch { toast.info(url); }
  };

  const openPortal = async () => {
    const p = portal || (await openPortalLink());
    window.open(`${window.location.origin}${p.portal_url}`, "_blank");
  };

  if (!data) return <div className="text-sm text-muted-foreground">Carregando…</div>;
  const { client, vehicles, jobs } = data;

  return (
    <div className="space-y-6" data-testid="cliente-detalhe-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2"><ArrowLeft className="w-4 h-4" /> Voltar</Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-client-btn">
            <Pencil className="w-3.5 h-3.5" /> Editar Cliente
          </Button>
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="portal-copy-btn"><Copy className="w-3.5 h-3.5" /> Copiar link do Portal</Button>
          <Button size="sm" onClick={openPortal} data-testid="portal-open-btn"><ExternalLink className="w-3.5 h-3.5" /> Abrir Portal</Button>
        </div>
      </div>

      <div className="border border-border rounded-md bg-card p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{client.tipo}</div>
            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="client-name">{client.name}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              {client.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{client.phone}</div>}
              {client.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{client.email}</div>}
              {client.address && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{client.address}</div>}
            </div>
            {client.observacoes && (
              <div className="mt-3 text-xs text-muted-foreground border-l-2 border-border pl-3 max-w-xl">
                {client.observacoes}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="border border-border rounded-md px-4 py-2">
              <div className="font-display text-2xl font-bold">{vehicles.length}</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-widest">Veículos</div>
            </div>
            <div className="border border-border rounded-md px-4 py-2">
              <div className="font-display text-2xl font-bold">{jobs.length}</div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-widest">Jobs</div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold tracking-tight mb-3">Veículos do cliente</h2>
        {vehicles.length === 0 ? <div className="text-sm text-muted-foreground">Nenhum veículo cadastrado.</div> :
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {vehicles.map((v) => (
              <button key={v.id} onClick={() => navigate(`/veiculos/${v.id}`)} className="border border-border rounded-md bg-card overflow-hidden text-left hover:-translate-y-0.5 transition-transform" data-testid={`client-vehicle-${v.id}`}>
                <div className="h-28 bg-secondary">
                  {v.photo_url ? <img src={v.photo_url} alt="" className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center"><Car className="w-6 h-6 text-muted-foreground" /></div>}
                </div>
                <div className="p-3">
                  <div className="font-display font-semibold text-sm">{v.year} {v.make} {v.model}</div>
                  <div className="text-xs text-muted-foreground font-mono">{v.plate || "—"} · {v.vin?.slice(-6) || "—"}</div>
                </div>
              </button>
            ))}
          </div>}
      </div>

      <div>
        <h2 className="font-display text-xl font-bold tracking-tight mb-3">Histórico de Jobs</h2>
        {jobs.length === 0 ? <div className="text-sm text-muted-foreground">Nenhum atendimento.</div> :
          <div className="border border-border rounded-md divide-y divide-border">
            {jobs.map((j) => (
              <div key={j.id} className="px-4 py-3 flex items-center gap-3 text-sm">
                <Wrench className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1">{j.service_type}</div>
                <div className="text-xs text-muted-foreground font-mono">{new Date(j.created_at).toLocaleDateString("pt-BR")}</div>
                <div className="text-xs px-2 py-0.5 rounded bg-secondary">{j.status}</div>
              </div>
            ))}
          </div>}
      </div>

      <EditClientDialog
        open={editing}
        client={client}
        onOpenChange={setEditing}
        onSaved={load}
      />
    </div>
  );
}

function EditClientDialog({ open, client, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && client) {
      setForm({
        name: client.name || "",
        phone: client.phone || "",
        email: client.email || "",
        address: client.address || "",
        tipo: client.tipo || "Particular",
        observacoes: client.observacoes || "",
      });
    }
  }, [open, client]);

  if (!open || !form) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nome do cliente é obrigatório");
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        tipo: form.tipo,
        observacoes: form.observacoes.trim() || null,
      });
      toast.success("Cliente atualizado");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
          <DialogDescription>Estas alterações afetam <b>todos os veículos</b> deste cliente. Para transferir apenas um veículo específico para outro cliente, use o botão "Transferir Cliente" na página do veículo.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Nome</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
              data-testid="edit-client-name"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Telefone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(00) 00000-0000"
                data-testid="edit-client-phone"
              />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="edit-client-email"
              />
            </div>
          </div>
          <div>
            <Label>Endereço</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="edit-client-address"
            />
          </div>
          <div>
            <Label>Tipo de cliente</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger data-testid="edit-client-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              rows={3}
              data-testid="edit-client-observacoes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="edit-client-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
